import browserConfig from "./browserConfig.json";
// import { getLogger } from "../../../utils/UnifiedLogger";

// const { logInfo } = getLogger("BrowserUtils", false);
const logInfo = (tag, message, data = {}) => {
        console.log(`[${tag}] ${message}`, data);
    };

export const detectBrowser = () => {
  try {
    if (typeof navigator === "undefined" || !navigator.userAgent) {
      return "default";
    }

    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes("firefox")) {
      return "firefox";
    }

    if (
      userAgent.includes("safari") &&
      !userAgent.includes("chrome") &&
      !userAgent.includes("crios") &&
      !userAgent.includes("edg") &&
      !userAgent.includes("edge")
    ) {
      return "safari";
    }

    if (userAgent.includes("edg/") || userAgent.includes("edge/")) {
      return "edge";
    }

    if (
      userAgent.includes("chrome") ||
      userAgent.includes("crios") ||
      userAgent.includes("chromium")
    ) {
      return "chrome";
    }

    if (userAgent.includes("opera") || userAgent.includes("opr/")) {
      return "opera";
    }

    if (userAgent.includes("msie") || userAgent.includes("trident")) {
      return "ie";
    }

    return "default";
  } catch (error) {
    logInfo("BROWSER_INFO", "Browser detection failed", {
      error: error.message,
    });
    return "default";
  }
};

export const getBrowserConfig = (configType, browser = null) => {
  const detectedBrowser = browser || detectBrowser();
  const config = browserConfig[configType];

  if (!config) {
    console.warn(`Configuration type '${configType}' not found, using default`);
    return {};
  }

  return config[detectedBrowser] || config.default || {};
};

export const getBrowserCapabilities = () => {
  const browser = detectBrowser();

  let supportsAudioWorklet = false;
  try {
    supportsAudioWorklet = !!(
      window.AudioContext && "audioWorklet" in AudioContext.prototype
    );
  } catch (error) {
    supportsAudioWorklet = false;
    console.debug("AudioWorklet not supported:", error.message);
  }

  let supportsAudioContext = false;
  try {
    supportsAudioContext = !!(window.AudioContext || window.webkitAudioContext);
  } catch (error) {
    console.debug("AudioContext check failed:", error.message);
  }

  const capabilities = {
    browser,
    supportsWebRTC: !!(
      navigator.mediaDevices && navigator.mediaDevices.getUserMedia
    ),
    supportsAudioContext,
    supportsScriptProcessor: true,
    supportsAudioWorklet,
    supportsWebSocket: !!(window.WebSocket || window.MozWebSocket),
    supportsPromises: typeof Promise !== "undefined",
    supportsAsyncAwait: true,
    supportsES6: true,

    firefoxSpecific: {
      supportsMozPrefix: browser === "firefox",
      requiresPolling: browser === "firefox",
      hasAudioContextIssues: browser === "firefox",
      needsLargerBuffers: browser === "firefox",
      supportsAudioWorklet: supportsAudioWorklet && browser === "firefox",
    },

    safariSpecific: {
      requiresUserGesture: browser === "safari",
      hasAutoplayRestrictions: browser === "safari",
      needsWebkitPrefix: browser === "safari",
      hasTimingIssues: browser === "safari",
    },

    edgeSpecific: {
      isChromiumBased: browser === "edge",
      hasLegacySupport: browser === "edge",
      needsPolyfills: false,
    },
  };

  return capabilities;
};

export const applyBrowserFixes = () => {
  const browser = detectBrowser();
  logInfo("BROWSER_INFO", "Info of browser", browser);
  if (browser === "firefox") {
    if (
      window.AudioContext &&
      !window.AudioContext.prototype.createScriptProcessor
    ) {
      window.AudioContext.prototype.createScriptProcessor =
        window.AudioContext.prototype.createJavaScriptNode;
    }

    if (!navigator.mediaDevices && navigator.mozGetUserMedia) {
      navigator.mediaDevices = {
        getUserMedia: (constraints) => {
          return new Promise((resolve, reject) => {
            navigator.mozGetUserMedia(constraints, resolve, reject);
          });
        },
      };
    }
  }

  if (browser === "safari") {
    if (!window.AudioContext && window.webkitAudioContext) {
      window.AudioContext = window.webkitAudioContext;
    }

    if (!navigator.mediaDevices && navigator.webkitGetUserMedia) {
      navigator.mediaDevices = {
        getUserMedia: (constraints) => {
          return new Promise((resolve, reject) => {
            navigator.webkitGetUserMedia(constraints, resolve, reject);
          });
        },
      };
    }
  }

  if (!window.AudioContext && window.webkitAudioContext) {
    window.AudioContext = window.webkitAudioContext;
  }

  if (!navigator.mediaDevices) {
    navigator.mediaDevices = {};
  }

  if (!navigator.mediaDevices.getUserMedia) {
    const legacyGetUserMedia =
      navigator.webkitGetUserMedia ||
      navigator.mozGetUserMedia ||
      navigator.msGetUserMedia;

    if (legacyGetUserMedia) {
      navigator.mediaDevices.getUserMedia = function (constraints) {
        return new Promise((resolve, reject) => {
          try {
            if (typeof legacyGetUserMedia === "function") {
              legacyGetUserMedia.call(navigator, constraints, resolve, reject);
            } else {
              legacyGetUserMedia(constraints, resolve, reject);
            }
          } catch (error) {
            reject(error);
          }
        });
      };
    } else {
      navigator.mediaDevices.getUserMedia = function () {
        return Promise.reject(new Error("getUserMedia is not supported"));
      };
    }
  }
};

export const getOptimizedSettings = () => {
  const browser = detectBrowser();

  return {
    audio: getBrowserConfig("audio", browser),
    audioContext: getBrowserConfig("audioContext", browser),
    scriptProcessor: getBrowserConfig("scriptProcessor", browser),
    vad: getBrowserConfig("vad", browser),
    socket: getBrowserConfig("socket", browser),
    timing: getBrowserConfig("timing", browser),
    quality: getBrowserConfig("quality", browser),
    buffers: getBrowserConfig("buffers", browser),
  };
};

export const getRequiredWorkarounds = () => {
  const browser = detectBrowser();

  return {
    needsPolling: browser === "firefox",
    needsUserGesture: browser === "safari",
    needsLargerBuffers: browser === "firefox",
    needsDelayedStart: browser === "safari",
    needsExtendedTimeout: browser === "firefox",
    needsManualResume: browser === "safari",
    needsPolyfills: ["edge", "default"].includes(browser),
    hasTimingIssues: ["firefox", "safari"].includes(browser),
    hasAudioContextBugs: browser === "firefox",
    hasAutoplayRestrictions: browser === "safari",
  };
};

export const logBrowserInfo = () => {
  const browser = detectBrowser();
  const capabilities = getBrowserCapabilities();
  const workarounds = getRequiredWorkarounds();

  logInfo("BROWSER_INFO", "Browser information", {
    browser,
    userAgent: navigator.userAgent,
    vendor: navigator.vendor,
    capabilities,
    workarounds,
  });

  return { browser, capabilities, workarounds };
};

export const validateBrowserCompatibility = () => {
  const capabilities = getBrowserCapabilities();
  const issues = [];
  const warnings = [];

  if (!capabilities.supportsWebRTC) {
    issues.push("WebRTC/getUserMedia not supported");
  }

  if (!capabilities.supportsAudioContext) {
    issues.push("AudioContext not supported");
  }

  if (!capabilities.supportsWebSocket) {
    issues.push("WebSocket not supported");
  }

  if (capabilities.browser === "firefox") {
    warnings.push(
      "Firefox detected - using optimized settings for better performance"
    );
  }

  if (capabilities.browser === "safari") {
    warnings.push("Safari detected - user gesture may be required for audio");
  }

  return {
    compatible: issues.length === 0,
    issues,
    warnings,
    browser: capabilities.browser,
    recommendations:
      issues.length > 0
        ? ["Please use a modern browser like Chrome, Firefox, Safari, or Edge"]
        : ["Browser is compatible with all features"],
  };
};

export const getOptimizedAudioConstraints = () => {
  const browser = detectBrowser();

  // Default constraints with noise suppression
  const baseConstraints = {
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
      sampleRate: 16000, // typical for ASR models
    },
  };

  // Browser-specific tweaks
  if (browser === "safari") {
    baseConstraints.audio.noiseSuppression = false; // Safari often ignores this
  }

  if (browser === "firefox") {
    baseConstraints.audio.channelCount = 2; // Firefox sometimes prefers stereo
  }

  return baseConstraints;
};

export default {
  detectBrowser,
  getBrowserConfig,
  getBrowserCapabilities,
  applyBrowserFixes,
  getOptimizedSettings,
  getRequiredWorkarounds,
  logBrowserInfo,
  validateBrowserCompatibility,
};
